import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Trash2, Users, Lock, Copy, Mail, Building2, Send, Clock } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

const ROLE_LABEL = { owner: "tm.roleOwner", admin: "tm.roleAdmin", staff: "tm.roleStaff" };

function copyLink(link, t) {
  navigator.clipboard?.writeText(link).then(
    () => toast.success(t("tm.copyOk")),
    () => toast.error(t("tm.copyFail")));
}

export default function Team() {
  const { partner } = useAuth();
  const { t } = useLang();
  const [members, setMembers] = useState(null);
  const [establishments, setEstablishments] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "staff", establishment_id: "" });
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  async function load() {
    try {
      const r = await api("/team/members");
      setMembers((r.members || []).filter((m) => m.role !== "owner" && !m.is_owner));
    } catch (e) {
      if (/propriétaire|403|Réservé|owner|reserved/i.test(e.message)) setForbidden(true);
      else toast.error(e.message);
    }
  }
  async function loadEstablishments() {
    try {
      const r = await api("/establishments");
      const list = Array.isArray(r) ? r : (r.establishments || []);
      setEstablishments(list);
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); loadEstablishments(); }, []);

  async function add(e) {
    e.preventDefault();
    if (!form.establishment_id) { toast.error(t("tm.chooseEst")); return; }
    setBusy(true);
    try {
      const res = await api("/team/members", { method: "POST", body: { ...form, email: form.email.trim().toLowerCase() } });
      toast.success(t("tm.inviteSent"));
      setLastInvite({ email: res.email, link: res.invite_link });
      setOpen(false);
      setForm({ name: "", email: "", role: "staff", establishment_id: "" });
      load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  }
  async function remove(id) {
    try { await api(`/team/members/${id}`, { method: "DELETE" }); toast.success(t("tm.memberRemoved")); load(); }
    catch (e) { toast.error(e.message); }
  }
  async function changeRole(id, role) {
    try { await api(`/team/members/${id}`, { method: "PATCH", body: { role } }); toast.success(t("tm.roleUpdated")); load(); }
    catch (e) { toast.error(e.message); }
  }
  async function resend(id, email) {
    try { const r = await api(`/team/members/${id}/resend`, { method: "POST" }); toast.success(t("tm.inviteResent")); setLastInvite({ email, link: r.invite_link }); }
    catch (e) { toast.error(e.message); }
  }

  if (forbidden) {
    return (
      <div className="p-6 md:p-10 max-w-3xl mx-auto">
        <PageHeader title={t("db.team.t")} />
        <div className="text-center py-24 border-2 border-dashed border-border rounded-2xl">
          <Lock size={32} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">{t("tm.forbidden")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto">
      <PageHeader title={t("db.team.t")} subtitle={t("db.team.s")} testid="team-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl font-semibold" data-testid="team-add-btn"><UserPlus size={18} className="mr-1.5" /> {t("tm.add")}</Button>
            </DialogTrigger>
            <DialogContent data-testid="team-add-dialog">
              <DialogHeader><DialogTitle className="font-display">{t("tm.inviteTitle")}</DialogTitle><DialogDescription>{t("tm.inviteDesc")}</DialogDescription></DialogHeader>
              <form onSubmit={add} className="findo-numbered space-y-5">
                <div><Label>{t("tm.name")}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="member-name" /></div>
                <div><Label>{t("a.email")}</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 rounded-xl" data-testid="member-email" /></div>
                <div><Label>{t("est.label")}</Label>
                  <Select value={form.establishment_id} onValueChange={(v) => setForm({ ...form, establishment_id: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-full" data-testid="member-establishment"><SelectValue placeholder={t("tm.chooseEstPh")} /></SelectTrigger>
                    <SelectContent>{establishments.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.city ? ` · ${e.city}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>{t("tm.role")}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1.5 h-11 rounded-full" data-testid="member-role"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="staff">{t("tm.staffOpt")}</SelectItem><SelectItem value="admin">{t("tm.adminOpt")}</SelectItem></SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-400">{t("tm.emailHint")}</p>
                <Button type="submit" disabled={busy} className="w-full rounded-xl font-semibold" data-testid="member-submit">{busy ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("tm.sending")}</span> : t("tm.sendInvite")}</Button>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {lastInvite?.link && (
        <div className="bg-findo-indigo/5 border border-findo-indigo/20 rounded-2xl p-4 mb-4 flex items-center gap-3" data-testid="invite-link-banner">
          <Mail size={18} className="text-findo-indigo shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-findo-navy">{t("tm.inviteCreated")}{lastInvite.email ? t("tm.forEmail").replace("{email}", lastInvite.email) : ""}</p>
            <p className="text-xs text-slate-500 truncate">{lastInvite.link}</p>
          </div>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={() => copyLink(lastInvite.link, t)} data-testid="copy-invite-link"><Copy size={14} className="mr-1" /> {t("tm.copyLink")}</Button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border card-shadow divide-y divide-border" data-testid="team-list">
        <Row name={partner?.org_name} email={partner?.email} role="owner" isOwner />
        {members?.map((m) => (
          <Row key={m.id} id={m.id} name={m.name} email={m.email} role={m.role}
               status={m.status} establishment={m.establishment_name}
               invitedAt={m.invited_at} expiresAt={m.invite_expires_at}
               onRemove={() => remove(m.id)} onRole={(r) => changeRole(m.id, r)} onResend={() => resend(m.id, m.email)} />
        ))}
        {members?.length === 0 && (
          <div className="p-8 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
            <Users size={28} className="text-slate-300" /> {t("tm.noMembers")}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ id, name, email, role, status, establishment, invitedAt, expiresAt, onRemove, onRole, onResend, isOwner }) {
  const { t, lang } = useLang();
  const fmt = (d) => d ? new Date(d).toLocaleDateString(lang || "en", { day: "2-digit", month: "short", year: "numeric" }) : null;
  const expired = expiresAt && new Date(expiresAt) < new Date();
  return (
    <div className="flex items-center gap-4 p-4">
      <div className="h-10 w-10 rounded-full findo-gradient grid place-items-center text-white font-bold">{(name || "?")[0]?.toUpperCase()}</div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 truncate flex items-center gap-2">
          {name}
          {status === "invited" && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{t("tm.invitePending")}</Badge>}
        </p>
        <p className="text-xs text-slate-400 truncate">{email}</p>
        {establishment && <p className="text-xs text-findo-indigo flex items-center gap-1 mt-0.5"><Building2 size={11} /> {establishment}</p>}
        {status === "invited" && (invitedAt || expiresAt) && (
          <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5" data-testid={`invite-dates-${id}`}>
            <Clock size={11} />
            {invitedAt && <>{t("tm.invitedOn").replace("{date}", fmt(invitedAt))}</>}
            {expiresAt && (
              <span className={expired ? "text-findo-danger font-semibold" : ""}>
                {" · "}{expired ? t("tm.linkExpired") : t("tm.expiresOn").replace("{date}", fmt(expiresAt))}
              </span>
            )}
          </p>
        )}
      </div>
      {isOwner ? (
        <Badge className="bg-findo-indigo/10 text-findo-indigo">{t(ROLE_LABEL[role]) || role}</Badge>
      ) : (
        <Select value={role} onValueChange={(v) => onRole && onRole(v)}>
          <SelectTrigger className="h-9 w-32 rounded-full" data-testid={`member-role-${id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="staff">{t("tm.roleStaff")}</SelectItem>
            <SelectItem value="admin">{t("tm.roleAdmin")}</SelectItem>
          </SelectContent>
        </Select>
      )}
      {!isOwner && status === "invited" && onResend && (
        <button onClick={onResend} title={t("tm.resendTitle")} className="text-findo-indigo hover:text-findo-navy" data-testid={`resend-member-${id}`}><Send size={16} /></button>
      )}
      {!isOwner && onRemove && (
        <button onClick={onRemove} className="text-red-500 hover:text-red-700" data-testid={`remove-member-${id}`}><Trash2 size={16} /></button>
      )}
    </div>
  );
}
